import { ContactOrganizerButton } from '@eventbrite/contact-organizer-button';
import { Avatar } from '@eventbrite/eds-avatar';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { SocialLinksStyle } from '../../../types';
import { EventDetailsSectionTitle } from '../../EventDetails/EventDetails';
import { SocialLinks } from '../../SocialLinks';
import {
    ReadMore,
    ReadMoreContent,
    ReadMoreToggle,
} from '../../_shared/ReadMore/';
import { FollowButton, useFollowCount } from '../FollowButton/FollowButton';
import { OrganizerStats, StyleTypes } from '../OrganizerStats/OrganizerStats';
import './DescriptiveOrganizerInfo.scss';

export interface DescriptiveOrganizerInfoProps {
    captchaKey: string;
    eventId: string;
    eventName: string;
    gaCategory: string;
    isAuthenticated: boolean;
    organizerAvatar?: string;
    organizerDescription?: string;
    organizerId: string;
    organizerName: string;
    organizerUrl: string;
    organizerFacebook?: string;
    organizerTwitter?: string;
    organizerWebsite?: string;
}

export const DescriptiveOrganizerInfo: React.FunctionComponent<
    DescriptiveOrganizerInfoProps
> = ({
    captchaKey,
    eventId,
    eventName,
    gaCategory,
    isAuthenticated,
    organizerAvatar,
    organizerDescription,
    organizerFacebook,
    organizerId,
    organizerName,
    organizerTwitter,
    organizerUrl,
    organizerWebsite,
}) => {
    const followersCount = useFollowCount();

    return (
        <section aria-label="Organizer profile">
            <EventDetailsSectionTitle>
                <h2>{gettext('About the organizer')}</h2>
            </EventDetailsSectionTitle>
            <div className="descriptive-organizer-info">
                {organizerAvatar && (
                    <div className="descriptive-organizer-info__avatar">
                        <Avatar imageUrl={organizerAvatar} />
                    </div>
                )}
                {organizerName && (
                    <div
                        className="descriptive-organizer-info__name"
                        data-testid="organizer-name"
                    >
                        <div className="descriptive-organizer-info__name-by">
                            {/* Translators: Organized by {organizerName} */}
                            {gettext('Organized by')}{' '}
                        </div>
                        <a
                            href={organizerUrl}
                            className="descriptive-organizer-info__name-link"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {organizerName}
                        </a>
                    </div>
                )}

                <OrganizerStats
                    style={StyleTypes.EXPANDED}
                    organizer={{ id: organizerId }}
                    followersCount={followersCount || null}
                />

                <ul className="descriptive-organizer-info__actions">
                    <li>
                        <ContactOrganizerButton
                            {...{
                                organizerId: organizerId,
                                organizerName: organizerName,
                                captchaKey: captchaKey,
                                isVisible: false,
                                isAuthenticated: isAuthenticated,
                                eventName: eventName,
                                eventId: eventId,
                                gaCategory: gaCategory,
                                domNodeId: 'event-page',
                            }}
                        />
                    </li>
                    {organizerName && (
                        <li>
                            <FollowButton />
                        </li>
                    )}
                </ul>

                {organizerDescription ? (
                    <ReadMore
                        ariaLabel={gettext('Organizer description')}
                        initialIsExpanded={false}
                    >
                        <div className="descriptive-organizer-info__description">
                            <ReadMoreContent
                                previewContentType="line"
                                previewLineCount={{
                                    mobile: 5,
                                    tablet: 5,
                                    desktop: 3,
                                }}
                            >
                                <div
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: organizerDescription,
                                    }}
                                ></div>
                            </ReadMoreContent>
                            <ReadMoreToggle />
                        </div>
                    </ReadMore>
                ) : null}

                <SocialLinks
                    orgWebsite={organizerWebsite}
                    orgTwitter={organizerTwitter}
                    orgFacebook={organizerFacebook}
                    style={SocialLinksStyle.rounded}
                />
            </div>
        </section>
    );
};
